import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { useActivityStore } from '../../../stores/activityStore';
import { useAuthStore } from '../../../stores/authStore';
import { getUserProfile } from '../../../services/firebase/userProfile';
import { ActivityGrid } from './ActivityGrid';

const COLLAPSED_HEIGHT = 'max-h-52'; // ~5 compact rows
const EXPANDED_HEIGHT = 'max-h-96';  // ~12 rows

export function ActivityWidget() {
  const { user } = useAuthStore();
  const { definitions, currentLog, weekDates, isLoading, initialize, toggleCompletion } =
    useActivityStore();

  const [activityEnabled, setActivityEnabled] = useState(true);
  const [showAll, setShowAll] = useState(false);

  // Load activity tracking enabled preference from user profile
  useEffect(() => {
    if (!user?.uid) return;
    getUserProfile(user.uid)
      .then(profile => {
        if (profile) {
          setActivityEnabled(profile.activityTrackingEnabled ?? true);
        }
      })
      .catch(() => {});
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) return;
    if (definitions.length === 0 && !isLoading) {
      initialize(user.uid, 0);
    }
  }, [user?.uid, definitions.length, isLoading, initialize]);

  if (!user?.uid || !activityEnabled) return null;

  // Sort: pinned first, then by order
  const activeDefs = definitions
    .filter(d => d.isActive)
    .sort((a, b) => {
      const aPinned = a.isPinned ?? false;
      const bPinned = b.isPinned ?? false;
      if (aPinned !== bPinned) return bPinned ? 1 : -1;
      return a.order - b.order;
    });

  const hasMore = activeDefs.length > 5;

  const totalSlots = activeDefs.reduce((sum, d) => sum + d.targetFrequency, 0);
  const completedSlots = activeDefs.reduce((sum, d) => {
    const count = (currentLog?.completions[d.id] ?? []).length;
    return sum + Math.min(count, d.targetFrequency);
  }, 0);
  const pct = totalSlots > 0 ? Math.round((completedSlots / totalSlots) * 100) : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.15 }}
      className="bg-white dark:bg-surface-800 rounded-xl border border-surface-200 dark:border-surface-700 p-4"
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-surface-900 dark:text-surface-100">
            This Week
          </h3>
          {!isLoading && totalSlots > 0 && (
            <p className="text-xs text-surface-500 dark:text-surface-400">
              {completedSlots} of {totalSlots} · {pct}%
            </p>
          )}
        </div>
        <Link
          to="/activity-log"
          className="flex items-center gap-1 text-xs text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300"
        >
          View All
          <ChevronRightIcon className="w-3.5 h-3.5" />
        </Link>
      </div>

      {isLoading && (
        <div className="flex justify-center py-6">
          <div className="w-5 h-5 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {!isLoading && activeDefs.length === 0 && (
        <p className="text-xs text-surface-500 dark:text-surface-400 text-center py-4">
          <Link to="/settings/activity-log" className="text-teal-600 dark:text-teal-400 underline">
            Set up your activity tracker
          </Link>
        </p>
      )}

      {!isLoading && currentLog && activeDefs.length > 0 && (
        <>
          <div
            className={`overflow-y-auto transition-all duration-300 ${
              showAll ? EXPANDED_HEIGHT : COLLAPSED_HEIGHT
            }`}
          >
            <ActivityGrid
              definitions={activeDefs}
              log={currentLog}
              weekDates={weekDates}
              compact
              totalsSource={activeDefs}
              onToggle={(activityId, dateStr, completed) =>
                toggleCompletion(user.uid!, activityId, dateStr, completed)
              }
            />
          </div>

          {hasMore && (
            <button
              type="button"
              onClick={() => setShowAll(v => !v)}
              className="mt-2 flex items-center gap-1 text-xs text-surface-500 dark:text-surface-400 hover:text-teal-600 dark:hover:text-teal-400 transition-colors"
            >
              <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${showAll ? 'rotate-180' : ''}`} />
              {showAll ? 'Show less' : `Show all (${activeDefs.length})`}
            </button>
          )}
        </>
      )}
    </motion.div>
  );
}
